import { forwardRef, useCallback } from 'react';

import { styled } from '@mui/material';
import dayjs from 'dayjs';

import { DATE_TIME } from '@cast/constants';
import {
  Button,
  Chip,
  Divider,
  DrawerProps,
  Icons,
  useDrawer,
  useDrawerInstance,
  ExternalLink,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { Notification, NotificationSeverity } from '@cast/types';

import { CopyToClipboard } from 'components/clickable-actions';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  Section,
} from 'components/drawer';
import { Linkify } from 'components/router';
import { useAbility } from '@cast/shared-ability-data-access';
import { useAckNotificationsMutation } from 'hooks/mutations/notifications';

import { NotificationDetails } from './_components';
import { rewriteLegacyConsoleUrl } from './_utils/rewriteLegacyConsoleUrl';
import { SeverityChip } from '../../_components';

const StyledSeverityChip = styled(SeverityChip)(({ theme }) => ({
  ...theme.typography.P12M,
}));

const ObsoleteChip = () => {
  return (
    <Chip
      sx={{
        color: tokens.theme.fg.status.onNeutralSubtle,
        backgroundColor: tokens.theme.bg.status.neutralSubtle,
        typography: 'P10M',
      }}
      size="sm"
      startIcon={<Icons.Clock fontSize={12} />}
    >
      Obsolete
    </Chip>
  );
};

type Props = DrawerProps & {
  notification?: Notification;
};

type NotificationProps = {
  notification: Notification;
};

const ClusterIdDetails = ({ notification }: NotificationProps) => {
  return (
    <Section
      title="CLUSTER / ID:"
      detail={
        <Stack direction="row" gap="4px">
          <Typography variant="P14R">
            {notification.clusterMetadata?.name}
          </Typography>
          {notification.clusterMetadata?.id && (
            <>
              <Typography color={tokens.theme.fg.structural.secondary}>
                /
              </Typography>
              <CopyToClipboard
                text={notification.clusterMetadata.id}
                iconSize="20px"
              />
            </>
          )}
        </Stack>
      }
    />
  );
};

const AckedHeader = ({ notification }: NotificationProps) => {
  return (
    <>
      <Typography variant="h5">{notification.name}</Typography>
      <Typography variant="P12R" color={tokens.theme.fg.structural.secondary}>
        Resolved by: {notification.ackedBy}
      </Typography>
      <StyledSeverityChip
        severity={notification.severity!}
        size="large"
        sx={{ mt: '12px' }}
      />
    </>
  );
};

const NonAckedHeader = ({ notification }: NotificationProps) => {
  return (
    <>
      <Typography variant="h5">{notification.name}</Typography>
      <Stack
        direction="row"
        justifyContent="space-between"
        mt="12px"
        alignItems="flex-end"
      >
        <StyledSeverityChip severity={notification.severity!} size="large" />
        <Stack direction="row" gap={8} alignItems="center">
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            {dayjs(notification.timestamp).format(DATE_TIME)}
          </Typography>
          {notification.isExpired && <ObsoleteChip />}
        </Stack>
      </Stack>
    </>
  );
};

const NotificationDrawer = forwardRef<HTMLDivElement, Props>(
  ({ notification, ...drawerProps }, ref) => {
    const { close } = useDrawerInstance();
    const ability = useAbility();

    const { mutate } = useAckNotificationsMutation(close);
    const isAcked = notification && !!notification.ackedBy;

    const handleResolveEvent = useCallback(() => {
      if (notification?.id) {
        mutate([notification.id]);
      }
    }, [notification, mutate]);

    if (!notification) {
      return null;
    }

    return (
      <Drawer
        {...drawerProps}
        ref={ref}
        testId="notifications-drawer"
        size="medium"
      >
        <DrawerHeader noDivider sticky={false}>
          <Stack mt={21}>
            {isAcked ? (
              <AckedHeader notification={notification} />
            ) : (
              <NonAckedHeader notification={notification} />
            )}
          </Stack>
        </DrawerHeader>
        <DrawerContent px={32}>
          <Divider sx={{ mt: '24px' }} />
          <Stack py={24}>
            {isAcked && (
              <Section
                title="TIMESTAMP:"
                detail={
                  <Stack direction="row" gap={8} alignItems="center">
                    <Typography variant="P14R">
                      {dayjs(notification.timestamp).format(DATE_TIME)}
                    </Typography>
                    {notification.isExpired && <ObsoleteChip />}
                  </Stack>
                }
              />
            )}
            <ClusterIdDetails notification={notification} />
            <Section
              title="OPERATION:"
              detail={notification.operationMetadata?.type}
            />
          </Stack>
          <Divider />
          <Typography
            variant="P14R"
            mt={24}
            sx={{
              whiteSpace: 'pre-line',
              '& a': {
                display: 'inline',
                whiteSpace: 'normal',
                '& svg': { fontSize: 12, transform: 'translateY(2px)' },
              },
            }}
          >
            <Linkify
              hrefDecorator={rewriteLegacyConsoleUrl}
              componentDecorator={
                notification.name === 'Cost anomaly detected'
                  ? (href) => (
                      <ExternalLink link={href}>cluster report</ExternalLink>
                    )
                  : undefined
              }
            >
              {notification.message?.replace(/\\n/g, '\n')}
            </Linkify>
          </Typography>
          {(notification.details?.length || 0) > 2 && (
            <NotificationDetails details={notification.details} />
          )}
          <Stack direction="row" justifyContent="flex-end" gap={16} py={24}>
            <Button variant="secondary" size="md" onClick={() => close()}>
              Close
            </Button>
            <Button
              size="md"
              disabled={ability.cannot('update', 'OrganizationNotifications')}
              onClick={handleResolveEvent}
              testId="resolve-button"
            >
              {[
                NotificationSeverity.INFO,
                NotificationSeverity.SUCCESS,
              ].includes(notification.severity as any)
                ? 'Acknowledge'
                : 'Resolve'}
            </Button>
          </Stack>
        </DrawerContent>
      </Drawer>
    );
  }
);

NotificationDrawer.displayName = 'NotificationDrawer';

export const useNotificationDrawer = () => {
  const { open } = useDrawer();
  return {
    openNotificationDrawer: ({
      notification,
    }: {
      notification: Notification;
    }) => {
      open({
        component: NotificationDrawer,
        props: {
          notification,
        },
      });
    },
  };
};
